import React, { useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import type { TenantCampaignWithCapabilitiesDto } from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import { myCampaignsQuery } from '@/hooks/queries';
import { ExpressLinkCard } from '@/components/express';

/**
 * Full-screen gate shown when no campaign is selected.
 * Auto-selects if user has exactly one campaign; otherwise shows a picker.
 */
export const CampaignGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const selectCampaign = useCampaignStore((s) => s.selectCampaign);
  const autoSelectAttempted = useRef(false);

  const api = getApiClient();

  const { data: campaigns = [], isLoading } = useQuery({ ...myCampaignsQuery(), enabled: !selectedCampaign });

  const { mutate: switchCampaign, isPending: isSwitching } = useMutation({
    mutationFn: async (campaignId: string) => {
      return api.tenantCampaigns.switchCampaign({ campaignId });
    },
    onSuccess: (data) => {
      selectCampaign(data.campaign);
      window.location.reload();
    },
  });

  // Auto-select if exactly one campaign
  useEffect(() => {
    if (
      !isLoading &&
      !selectedCampaign &&
      campaigns.length === 1 &&
      !autoSelectAttempted.current
    ) {
      autoSelectAttempted.current = true;
      switchCampaign(campaigns[0].id);
    }
  }, [campaigns, isLoading, selectedCampaign, switchCampaign]);

  if (selectedCampaign) {
    return <>{children}</>;
  }

  if (isLoading || isSwitching) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-3">
          <div className="h-8 w-8 mx-auto animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
          <p className="text-sm text-gray-500">
            {isSwitching ? 'Switching campaign...' : 'Loading campaigns...'}
          </p>
        </div>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4 max-w-sm">
          <div className="text-4xl">📋</div>
          <h2 className="text-lg font-semibold text-gray-900">No Campaigns Available</h2>
          <p className="text-sm text-gray-500">
            You don't have access to any campaigns yet. Contact your campaign administrator to get invited.
          </p>
          {/* Settings needs a campaign, so a Consultant who has none finds their link here. */}
          <div className="text-left">
            <ExpressLinkCard />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-md space-y-6 px-4">
        <div className="text-center space-y-2">
          <h2 className="text-xl font-semibold text-gray-900">Select a Campaign</h2>
          <p className="text-sm text-gray-500">Choose a campaign to get started.</p>
        </div>

        <div className="space-y-2">
          {campaigns.map((campaign) => (
            <CampaignOption
              key={campaign.id}
              campaign={campaign}
              onSelect={() => switchCampaign(campaign.id)}
              disabled={isSwitching}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

function CampaignOption({
  campaign,
  onSelect,
  disabled,
}: {
  campaign: TenantCampaignWithCapabilitiesDto;
  onSelect: () => void;
  disabled: boolean;
}) {
  return (
    <button
      onClick={onSelect}
      disabled={disabled}
      className="w-full text-left rounded-lg border border-gray-200 bg-white p-4 hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="font-medium text-gray-900 truncate">{campaign.name}</p>
          {campaign.description && (
            <p className="text-sm text-gray-500 truncate">{campaign.description}</p>
          )}
        </div>
        <span className="ml-3 shrink-0 text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-600">
          {campaign.isOwner ? 'Owner' : (campaign.presetKey ?? 'Custom')}
        </span>
      </div>
    </button>
  );
}
